import { apiApiUser } from "@/api/api-user.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiApiTokenGenerate, ApiApiUserFilter, ApiApiUserSave } from "@/types/api-user.types.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const apiUsersKeys = createQueryKeys("api-users", {
  list: (filter: ApiApiUserFilter = {}) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await apiApiUser.search(filter);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
  }),
});

export const useApiUsers = (filter: ApiApiUserFilter = {}, enabled: boolean = true) => {
  return useQuery({
    ...apiUsersKeys.list(filter),
    enabled,
  });
};

export interface ApiUserSaveParams {
  id: number;
  payload: ApiApiUserSave;
}

export const useApiUserSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: ApiUserSaveParams) {
      const result =
        id == 0 ? await apiApiUser.create(payload) : await apiApiUser.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiUsersKeys.list._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export interface ApiTokenGenerateParams {
  apiUserId: number;
  payload: ApiApiTokenGenerate;
}

export const useApiTokenGenerate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ apiUserId, payload }: ApiTokenGenerateParams) {
      const result = await apiApiUser.generateToken(apiUserId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useApiUserDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function (id: number) {
      const { success, error } = await apiApiUser.delete(id);

      if (error || !success) throw new ApiErrorException(error);

      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiUsersKeys.list._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
